import axios from 'axios'
import * as HttpStatus from 'http-status-codes'

import { toCamelCase } from '../helpers/utils'
import {
  error,
  errorD1,
  information,
  kellyGreen,
  mangoYellow,
  paleyellow,
  royalBlue,
  success,
} from '../theme/colors'

export interface SafetyScoreV3 {
  userId: string
  accidentsViolationsScore: number | null
  accidentsViolationsLabel: string | null
  accidentsViolationsColor: string | null
  drivingBehaviorScore: number | null
  drivingBehaviorLabel: string | null
  drivingBehaviorColor: string | null
  onRoadComplianceScore: number | null
  onRoadComplianceLabel: string | null
  onRoadComplianceColor: string | null
  safetyScore: number | null
  safetyScoreLabel: string | null
  safetyScoreColor: string | null
  safetyScoreBackgroundColor: string | null
  runTs: string | null
}

export enum SafetyEventCategory {
  COMPLIANCE = 'COMPLIANCE',
  ACCIDENTS_AND_VIOLATIONS = 'ACCIDENTS_AND_VIOLATIONS',
  DRIVING_BEHAVIORS = 'DRIVING_BEHAVIORS',
}

export interface SafetyEvent {
  userId: string
  eventType: string
  eventCategory: SafetyEventCategory | null
  eventStart: string
  eventEnd: string
  eventDetails: string
  state: string | null
  city: string | null
  violationCode: string | null
  inspectionLevel: number | null
  isOutOfService: boolean | null
  fatalities: number | null
  injuries: number | null
  towAway: boolean | null
  citationIssued: string | null
  sfdcNotes: string | null
  eventId: string | null
  accidentsViolationsType: string | null
  reportDate: string | null
  sfdcReportDate: string | null
  title: string | null
  description: string | null
  primaryVin: string | null
  secondaryVin: string | null
  remainingPoints: number | null
  originalPoints: number | null
}

// This endpoint return 404 if the user does not have a safety score
export function getSafetyScoreForUser(userId: string): Promise<SafetyScoreV3 | undefined> {
  return axios.get(`/api/v3/compliance/safety-score/?user_id=${userId}`).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data ? (toCamelCase(response.data) as SafetyScoreV3) : undefined
    }

    if (response.status === HttpStatus.NOT_FOUND) {
      return undefined
    }
  })
}

// This endpoint doesn't return 404 if the user does not have any safety events
export function getSafetyEventsForUser(userId: string): Promise<SafetyEvent[] | undefined> {
  return axios.get(`/api/v3/compliance/safety-events/?user_id=${userId}`).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data ? (toCamelCase(response.data) as SafetyEvent[]) : undefined
    }
  })
}

export interface SafetyScoreRange {
  label: string
  min: number
  max: number
  color: string
  backgroundColor: string
  description: string
}

// Min is exclusive, max is inclusive
export const SAFETY_SCORE_RANGE_DEFAULTS: SafetyScoreRange[] = [
  {
    label: 'Excellent',
    min: 90,
    max: 100,
    color: kellyGreen,
    backgroundColor: success,
    description: 'Great job! There are no or little factors affecting your score.',
  },
  {
    label: 'Satisfactory',
    min: 80,
    max: 90,
    color: royalBlue,
    backgroundColor: information,
    description: 'Be attentive. Your score was impacted by some minor factors.',
  },
  {
    label: 'Needs Improvement',
    min: 70,
    max: 80,
    color: mangoYellow,
    backgroundColor: paleyellow,
    description:
      'Watch out. Certain factors need close monitoring and possible adjustment to improve your score.',
  },
  {
    label: 'Needs Attention',
    min: -0.01,
    max: 70,
    color: errorD1,
    backgroundColor: error,
    description: 'The Safety team will contact you to provide guidance on how to improve your score.',
  },
]

export function getSafetyScoreRanges(): Promise<SafetyScoreRange[]> {
  return axios
    .get('/api/v3/compliance/safety-score-ranges/')
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        if (response.data && Array.isArray(response.data)) {
          return response.data.map((x) => toCamelCase(x)) as SafetyScoreRange[]
        }
      }

      return SAFETY_SCORE_RANGE_DEFAULTS
    })
    .catch(() => {
      return SAFETY_SCORE_RANGE_DEFAULTS
    })
}
